import { Link } from 'react-router-dom';
import { Loader2, Network, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useCustomerGroup, useResetGroupOverrides } from '@/hooks/admin/useCustomerGroup';

/** Friendly labels for the inheritable fields when listing a store's overrides. */
const FIELD_LABELS: Record<string, string> = {
  billing_email: 'Primary email',
  remittance_email: 'Secondary email',
  optional_email: 'Optional email',
  accounting_email: 'Accounting email',
  invoice_recipients: 'Invoice routing',
  statement_recipients: 'Statement routing',
  reminder_recipients: 'Reminder routing',
  payment_terms: 'Payment terms',
  payment_method_orders: 'Orders payment method',
  payment_method_rentals: 'Rentals payment method',
  payment_method_tanks: 'Tanks payment method',
};

/**
 * Shown on a MEMBER STORE's detail tabs when its group master runs
 * "Master billing": accounting + payment settings inherit from the master;
 * any field edited here becomes a store-specific override (master syncs
 * skip it) until "Reset to master".
 */
export function GroupInheritanceBanner({ customerId }: { customerId: string }) {
  const { data } = useCustomerGroup(customerId);
  const reset = useResetGroupOverrides(customerId);

  const memberOf = data?.member_of;
  if (!memberOf || memberOf.billing_mode !== 'master') return null;

  const overrides = memberOf.overrides.map((f) => FIELD_LABELS[f] ?? f);

  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 space-y-1.5">
      <p className="text-[13px] text-sky-900 flex items-center gap-2 flex-wrap">
        <Network size={14} className="shrink-0" />
        <span>
          Accounting &amp; payment settings are managed by{' '}
          <Link
            to={`/admin/customers/${memberOf.master_customer_id}/overview`}
            className="font-semibold underline"
          >
            {memberOf.master_name}
          </Link>{' '}
          ({memberOf.group_kind === 'chain' ? 'Chain HQ' : 'Franchise owner'}
          {memberOf.group_brand ? ` · ${memberOf.group_brand}` : ''}, Master billing). Invoices
          always email this store's own Primary Email AND the master's Accounting address — every
          store gets a copy of its own invoice. Statements + paper mail are locked off — those
          change only on the master. Everything else you edit here becomes a store-specific
          override that master updates won't touch.
        </span>
      </p>
      {overrides.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[12px] text-sky-900">
            <b>
              {overrides.length} override{overrides.length === 1 ? '' : 's'}:
            </b>{' '}
            {overrides.join(', ')}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={reset.isPending}
            onClick={() => {
              if (
                window.confirm(
                  `Reset this store to ${memberOf.master_name}'s settings? All ${overrides.length} override${overrides.length === 1 ? '' : 's'} will be replaced by the master's values.`,
                )
              ) {
                reset.mutate();
              }
            }}
          >
            {reset.isPending ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
            Reset to master
          </Button>
        </div>
      )}
    </div>
  );
}
